/* ============================================================
   SAM-198 — business page, Activity tab: pure helpers (no React).

   Bookings (crud /booking) and access passes (/business_access/combined) are two
   records; the Activity list shows both as one visit timeline for the business,
   newest visit first. A booking row carries the booking status and a pass row the
   pass status. SAM-608: a pass granted by a booking carries `booking` (service,
   time, status), shown on the pass row with a link to the Bookings console; a
   booking row links to its pass when that pass is on the loaded page.

   Passes come from the server already filtered (business_id, visit window, status,
   search) and paginated; filterActivity leaves them alone when told so.

   Pass status ids as the backend and Invitations.tsx have them: 1 Pending, 2 Confirmed, 3 Verified,
   4 Checked In, 5 Checked Out, 6 Cancelled, 7 Expired, 8 Processing. (This file read them with the old
   dashboard mapping — 3 in, 6 out, 5 cancelled — so a cancelled pass showed «Checked out».)
   ============================================================ */
import type { Booking } from '../../hooks/useBookings';
import { BOOKING_STATUS_CANCELLED, getStatusLabel } from '../../hooks/useBookings';
import type { BusinessAccess } from '../../hooks/useBusinessAccessInvitations';
import {
  bookingConsoleLink, combinedBookingService, combinedBookingStatus, passDetailLink, toDayParam, type CombinedQuery,
} from '../../api/businessAccessCombined';

export const PASS_STATUS = { pending: 1, approved: 2, verified: 3, in: 4, out: 5, cancelled: 6, expired: 7, processing: 8 } as const;

/** Pass status names by id; anything else uses the row's own name. */
const PASS_LABELS: Record<number, string> = {
  1: 'Pending', 2: 'Confirmed', 3: 'Verified', 4: 'Checked in', 5: 'Checked out', 6: 'Cancelled', 7: 'Expired', 8: 'Processing',
};

/** `all` is no date window at all; the other three are relative to today. */
export type When = 'all' | 'today' | 'upcoming' | 'past';
/** The part of `When` a visit date can fall in. */
export type VisitWhen = Exclude<When, 'all'>;
export type PassFilter = 'any' | 'pending' | 'approved' | 'in' | 'out';
/** Which rows the list shows: everything, only bookings, or only passes (front-desk ones included). */
export type KindFilter = 'all' | 'bookings' | 'passes';

export type Tone = 'ok' | 'wait' | 'off' | 'bad' | 'info';

export interface ActivityRow {
  key: string;
  kind: 'booking' | 'pass';
  /** ISO date-time of the visit (booking reservation_start / pass start_date). */
  at: string;
  guest: string;
  what: string;
  guests: number;
  tariff: string;
  status: string;
  tone: Tone;
  passStatusId?: number;
  /** Pass rows: the pass id. Booking rows: the pass(es) this booking granted, when loaded. */
  passIds?: string[];
  /** The booking this row is (booking rows) or that granted the pass (pass rows). */
  bookingId?: string;
  /** Pass rows granted by a booking: what to show about that booking. */
  linkedBooking?: { service: string; at: string | null; status: string; code: string | null };
}

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/** today / upcoming / past relative to `now`, by local calendar day. */
export function whenOf(iso: string, now: Date = new Date()): VisitWhen {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'past';
  if (dayKey(d) === dayKey(now)) return 'today';
  return d > now ? 'upcoming' : 'past';
}

const bookingTone = (statusId: number): Tone =>
  statusId === 2 || statusId === 3 ? 'ok' : statusId === 1 ? 'wait' : statusId === 5 ? 'bad' : 'off';

export const passTone = (statusId: number): Tone =>
  statusId === PASS_STATUS.in ? 'ok'
    : statusId === PASS_STATUS.pending || statusId === PASS_STATUS.processing ? 'wait'
      : statusId === PASS_STATUS.approved || statusId === PASS_STATUS.verified ? 'info'
        : statusId === PASS_STATUS.cancelled ? 'bad' : 'off';

type Line = { total_guest?: number | string; line_type?: string; rate_group?: string };

/** Guests on a booking: the sum of its guest lines (unit and add-on lines are not people). */
export function bookingGuests(b: Pick<Booking, 'booking_options'>): number {
  const lines = (Array.isArray(b.booking_options) ? b.booking_options : []) as Line[];
  return lines
    .filter((l) => l && l.line_type !== 'add_on' && l.line_type !== 'unit')
    .reduce((n, l) => n + (Number(l.total_guest) || 0), 0);
}

/** The rate groups a booking was priced in (SAM-588), e.g. "dom", or "—". */
export function bookingTariff(b: Pick<Booking, 'booking_options'>): string {
  const lines = (Array.isArray(b.booking_options) ? b.booking_options : []) as Line[];
  const groups = Array.from(new Set(lines.map((l) => l?.rate_group).filter(Boolean) as string[]));
  return groups.length ? groups.join(', ') : '—';
}

export function buildActivityRows(
  bookings: Booking[],
  passes: BusinessAccess[],
  passLabel: (statusId: number) => string,
): ActivityRow[] {
  const rows: ActivityRow[] = [];
  bookings.forEach((b) => {
    if (!b.reservation_start) return;
    const statusId = Number(b.status_id);
    const passIds = passes.filter((p) => p.booking_id && String(p.booking_id) === String(b.id)).map((p) => String(p.id));
    rows.push({
      key: `b-${b.id}`,
      kind: 'booking',
      bookingId: String(b.id),
      ...(passIds.length ? { passIds } : {}),
      at: b.reservation_start,
      guest: b.user_name || b.user_email || '—',
      what: b.service_name || 'Booking',
      guests: bookingGuests(b),
      tariff: bookingTariff(b),
      status: getStatusLabel(statusId),
      tone: bookingTone(statusId),
    });
  });
  passes.forEach((p) => {
    const at = p.start_date || p.create_date;
    if (!at) return;
    const statusId = Number((p as { business_access_status_id?: number }).business_access_status_id ?? 0);
    const main = p.main_guest || ({} as BusinessAccess['main_guest']);
    const others = Array.isArray(p.business_guest) ? p.business_guest.length : 0;
    const bookingId = p.booking?.id || p.booking_id || undefined;
    rows.push({
      key: `p-${p.id}`,
      kind: 'pass',
      at: String(at),
      guest: [main?.name, main?.last_name].filter(Boolean).join(' ') || '—',
      what: p.type_name || 'Pass',
      guests: 1 + others,
      tariff: '—',
      status: PASS_LABELS[statusId] || p.status_name || passLabel(statusId),
      tone: passTone(statusId),
      passStatusId: statusId,
      passIds: [String(p.id)],
      ...(bookingId ? { bookingId: String(bookingId) } : {}),
      ...(p.booking ? {
        linkedBooking: {
          service: combinedBookingService(p.booking),
          at: p.booking.reservation_start || null,
          status: combinedBookingStatus(p.booking),
          code: p.booking.confirmation_code || null,
        },
      } : {}),
    });
  });
  return rows.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

const PASS_FILTER_IDS: Record<Exclude<PassFilter, 'any'>, number> = {
  pending: PASS_STATUS.pending, approved: PASS_STATUS.approved, in: PASS_STATUS.in, out: PASS_STATUS.out,
};

/**
 * `passesPrefiltered`: pass rows came from the server already narrowed to this window,
 * status and search (/combined), so only booking rows are filtered here. The server's window
 * is an overlap (a pass running since yesterday is also "today"), which a start-day check
 * here would undo.
 */
export function filterActivity(
  rows: ActivityRow[],
  when: When,
  passFilter: PassFilter,
  query: string,
  now: Date = new Date(),
  opts: { passesPrefiltered?: boolean } = {},
): ActivityRow[] {
  const q = query.trim().toLowerCase();
  return rows.filter((r) => {
    if (opts.passesPrefiltered && r.kind === 'pass') return true;
    if (when !== 'all' && whenOf(r.at, now) !== when) return false;
    if (passFilter !== 'any' && (r.kind !== 'pass' || r.passStatusId !== PASS_FILTER_IDS[passFilter])) return false;
    if (q && !`${r.guest} ${r.what}`.toLowerCase().includes(q)) return false;
    return true;
  });
}

/**
 * The kind filter. "passes" keeps every pass row — one granted by a booking and one issued at the
 * front desk without a booking alike; "bookings" keeps only booking rows.
 */
export function filterActivityKind(rows: ActivityRow[], kind: KindFilter): ActivityRow[] {
  if (kind === 'all') return rows;
  return rows.filter((r) => (kind === 'passes' ? r.kind === 'pass' : r.kind === 'booking'));
}

/** The row's kind as the list labels it: a pass with no booking behind it was issued at the front desk. */
export const activityKindLabel = (r: Pick<ActivityRow, 'kind' | 'bookingId'>): string =>
  r.kind === 'booking' ? 'Booking' : r.bookingId ? 'Pass' : 'Pass · front desk';

/** Pass status id for a pass filter, or undefined for "any". */
export const passFilterStatusId = (f: PassFilter): number | undefined =>
  f === 'any' ? undefined : PASS_FILTER_IDS[f];

const addDays = (d: Date, n: number) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() + n);
  return x;
};

/**
 * The /combined visit window for a When tab, in local days: today = today; upcoming = from
 * tomorrow on; past = up to yesterday. The server matches passes whose visit overlaps it.
 */
export function whenWindow(when: When, now: Date = new Date()): Pick<CombinedQuery, 'date_from' | 'date_to'> {
  if (when === 'all') return {};
  if (when === 'today') return { date_from: toDayParam(now), date_to: toDayParam(now) };
  if (when === 'upcoming') return { date_from: toDayParam(addDays(now, 1)) };
  return { date_to: toDayParam(addDays(now, -1)) };
}

/** The When tab a visit date falls in (for jumping to a deep-linked pass). */
export const whenForVisit = (iso: string | null | undefined, now: Date = new Date()): VisitWhen =>
  iso ? whenOf(iso, now) : 'today';

export interface ActivityKpis {
  bookingsToday: number;
  inVenue: number;
  pendingCheckIn: number;
  paidThisMonth: number;
}

type PaymentLike = { payment_status?: string; payment_amount?: number | string | null; payment_date?: string | null; createdAt?: string };

export function activityKpis(
  bookings: Booking[],
  passes: BusinessAccess[],
  payments: PaymentLike[],
  now: Date = new Date(),
): ActivityKpis {
  const statusOf = (p: BusinessAccess) => Number((p as { business_access_status_id?: number }).business_access_status_id ?? 0);
  const passDay = (p: BusinessAccess) => String(p.start_date || p.create_date || '');
  const month = (iso?: string | null) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime()) && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  };
  return {
    bookingsToday: bookings.filter((b) => b.reservation_start && whenOf(b.reservation_start, now) === 'today'
      && Number(b.status_id) !== BOOKING_STATUS_CANCELLED).length,
    inVenue: passes.filter((p) => statusOf(p) === PASS_STATUS.in).length,
    pendingCheckIn: passes.filter((p) => statusOf(p) === PASS_STATUS.approved && whenOf(passDay(p), now) === 'today').length,
    paidThisMonth: payments
      .filter((p) => p.payment_status === 'success' && month(p.payment_date || p.createdAt))
      .reduce((sum, p) => sum + (Number(p.payment_amount) || 0), 0),
  };
}

/**
 * Where a row of the Activity list opens: a booking, in the Bookings console (its detail panel); a pass, in the
 * Passes page (its detail). Both screens exist; the row only links to them.
 */
export function activityRowHref(row: Pick<ActivityRow, 'kind' | 'bookingId' | 'passIds'>, businessId: string): string | null {
  if (row.kind === 'booking') return row.bookingId ? bookingConsoleLink(row.bookingId, businessId) : null;
  const passId = row.passIds?.[0];
  return passId ? passDetailLink(passId) : null;
}

export type TileState = 'loading' | 'error' | 'ok';

/** A tile that has no number yet says so: «…» while it loads, «—» when its request failed. Never a made-up 0. */
export const tileState = (data: unknown, failed?: boolean): TileState => (data ? 'ok' : failed ? 'error' : 'loading');
export const tileText = (state: TileState, value: string): string => (state === 'loading' ? '…' : state === 'error' ? '—' : value);
